import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MetaHead from "./MetaHead";
import { API_ENDPOINTS } from "../config/api";

const VerifyEmail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get("token");

      if (!token) {
        setStatus("error");
        setMessage(String(t("verifyEmail.invalidToken")));
        return;
      }

      try {
        const response = await fetch(`${API_ENDPOINTS.AUTH.VERIFY_EMAIL}?token=${encodeURIComponent(token)}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();

        if (response.ok) {
          setStatus("success");
          setMessage(data.message || String(t("verifyEmail.success")));
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate("/login");
          }, 3000);
        } else {
          setStatus("error");
          setMessage(data.message || String(t("verifyEmail.error")));
        }
      } catch (error) {
        console.error("Email verification error:", error);
        setStatus("error");
        setMessage(String(t("verifyEmail.networkError")));
      }
    };

    verifyEmail();
  }, [searchParams, navigate, t]);

  return (
    <>
      <MetaHead
        title={t('verifyEmail.title')}
        description={t('verifyEmail.description') as string}
        keywords={t('verifyEmail.keywords') as string}
      />
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-accent-50 dark:from-neutral-950 dark:via-neutral-900 dark:to-neutral-950 flex items-center justify-center overflow-x-hidden transition-colors duration-300">
      <div className="max-w-md w-full mx-4">
        <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-xl p-8 text-center">
          {status === "loading" && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl shadow-glow mb-6">
                <svg
                  className="animate-spin w-8 h-8 text-white"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                {t("verifyEmail.verifying")}
              </h2>
              <p className="text-neutral-600 dark:text-neutral-400">
                {t("verifyEmail.verifyingDesc")}
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-2xl shadow-glow mb-6">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                {t("verifyEmail.successTitle")}
              </h2>
              <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                {message}
              </p>
              <p className="text-sm text-neutral-500 dark:text-neutral-500">
                {t("verifyEmail.redirecting")}
              </p>
            </>
          )}

          {status === "error" && (
            <>
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-2xl shadow-glow mb-6">
                <svg
                  className="w-8 h-8 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
                {t("verifyEmail.errorTitle")}
              </h2>
              <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                {message}
              </p>
              <button
                onClick={() => navigate("/login")}
                className="inline-flex items-center px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors duration-200"
              >
                {t("verifyEmail.backToLogin")}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
    </>
  );
};

export default VerifyEmail;
